import { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import Input from 'components/shared/form/Input';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { formatSsn, isPartialSsn, ssnDigits } from './ssn';

interface Props {
  // Digits only — see ssn.ts for why the stored shape is digits rather than the dashed form.
  digits: string;
  // What the referral held when the modal opened, exactly as stored. Shown verbatim when it's a
  // partial: the packet's own XXX-XX-7554 says which digits it has, which is the one thing the
  // digits alone can't. Kept raw for that reason — normalising it here would throw the X's away.
  storedValue: string;
  edited: boolean;
  invalid: boolean;
  onChange: (digits: string) => void;
  onStartEntry: () => void;
  testId: string;
}

// Where the caret belongs after reformatting: just past the nth digit. Counted in DIGITS rather than
// characters because the dashes move as the number fills — character 4 is a dash one keystroke and a
// digit the next, so a character offset drifts.
function caretAfterDigits(formatted: string, digitsBefore: number): number {
  if (digitsBefore === 0) return 0;

  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) seen += 1;
    if (seen === digitsBefore) return index + 1;
  }

  return formatted.length;
}

// The Social Security Number field.
//
// Two things make it its own component rather than another branch of the modal's field renderer.
//
// The caret. The value is reformatted on every keystroke, and a controlled input whose value changes
// under it puts the caret at the end — so correcting a digit in the middle threw the cursor to the
// end of the number. It's restored by digit position after each reformat.
//
// A partial from the packet. Packets often print the number masked (XXX-XX-7554), and extraction
// stores what it could read. It is NOT offered as an editable number: which part of the number the
// digits are is unknown to us — a packet masking the other end (123-45-XXXX) stores a first five
// just the same — so formatting them as the start of an SSN states something we don't know, and
// typing the rest around them would assemble a nine-digit number that could be wrong. The packet's
// own text is shown instead, in a field that can't be typed into, with replacing it a deliberate act.
export default function SsnField({ digits, storedValue, edited, invalid, onChange, onStartEntry, testId }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const partialOnFile = !edited && isPartialSsn(storedValue);

  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    if (caret === null || !inputRef.current) return;

    const position = caretAfterDigits(formatSsn(digits), caret);
    inputRef.current.setSelectionRange(position, position);
  }, [digits]);

  // Same field box as everything else on the row, so it sits on the line its neighbours do — and
  // disabled rather than merely read-only, because the greyed box is what says "this isn't yours to
  // type in" without a sentence explaining it. The packet's own masking does the rest of the
  // explaining, which is why there's no copy here beyond the action.
  if (partialOnFile) {
    return (
      <>
        {/* data-dd-privacy on every node that can hold real digits: session replay records the DOM
            as rendered (defaultPrivacyLevel is 'allow'), so the typed number, the packet's partial,
            and the "Replacing …" hint would all land in the replay without it (OLIO-2958). */}
        <Input value={storedValue} disabled data-dd-privacy='mask' data-testid='ssn-partial' />
        {/* "Partial", not "invalid": a packet masking the number is normal practice, not a mistake
            by anyone, and the person reading this may simply not have the rest. The line says what
            happened and what the action does — it doesn't ask them to go and fix something. */}
        <FieldHint>
          Partial number from the packet.{' '}
          <ReplaceLink type='button' onClick={onStartEntry} data-testid='ssn-replace'>
            Enter the full number
          </ReplaceLink>
        </FieldHint>
      </>
    );
  }

  return (
    <>
      <Input
        ref={inputRef}
        value={formatSsn(digits)}
        onChange={(event) => {
          const element = event.target;
          const upToCaret = element.value.slice(0, element.selectionStart ?? element.value.length);
          pendingCaret.current = ssnDigits(upToCaret).length;
          onChange(ssnDigits(element.value));
        }}
        $hasError={invalid}
        inputMode='numeric'
        autoComplete='off'
        placeholder='123-45-6789'
        data-dd-privacy='mask'
        data-testid={testId}
      />
      {invalid && <FieldError data-testid='ssn-error'>Enter all nine digits, or leave this blank.</FieldError>}
      {/* Kept on screen while they type: what's being replaced was the only copy, and someone
          part-way through the full number still needs to check it against the packet. */}
      {edited && isPartialSsn(storedValue) && (
        <FieldHint data-dd-privacy='mask' data-testid='ssn-replacing'>
          Replacing {storedValue}
        </FieldHint>
      )}
    </>
  );
}

// Sits inline at the end of the hint, so the explanation and the way out read as one line.
const ReplaceLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.textStyles.bodySmall.fontSize};
  line-height: ${({ theme }) => theme.textStyles.bodySmall.lineHeight};
  color: ${colors.blue100};
  cursor: pointer;
`;

const FieldError = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  color: ${colors.red100};
`;

const FieldHint = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  color: ${colors.black50};
`;
